
import { Getter, State } from 'vuex-class';
import { Component, Vue } from 'vue-property-decorator';
import { IdLookup } from '@/store/validations/types';
import { mixins } from "vue-class-component";
import { SaveResult } from '@/types';
import { DateUtilsMixin } from "@/mixins/date-utils-mixin";
import SaveToolbar from '@/components/shared/SaveToolbar.vue';
import SelectInput from '@/components/shared/SelectInput.vue';
import CheckboxInput from '@/components/shared/CheckboxInput.vue';
import NumberInput from '@/components/shared/NumberInput.vue';
import DateInput from '@/components/shared/DateInput.vue';
import SubSection from '@/components/shared/SubSection.vue';
import { GenericCodeValue } from '@/store/types';
import { HospitalOption, ACTIVE_REGION_TRANSPLANT_PROGRAM } from '@/store/hospitals/types';
import { NationalOrganWaitlistReportState, TransplantActivityReportQuery, odoCodes } from '@/store/reports/types';
import { Organ, BloodType, OrganCodeValue, BloodTypeValue } from '@/store/lookups/types';
import { User } from '@/store/users/types';
import NationalOrganWaitlistTable from '@/components/shared/NationalOrganWaitlistTable.vue';

@Component({
  components: {
    SaveToolbar,
    SelectInput,
    DateInput,
    SubSection,
    CheckboxInput,
    NumberInput,
    NationalOrganWaitlistTable
  }
})

export default class NationalOrganWaitlistReport extends mixins(DateUtilsMixin)  {
  // State
  @State(state => state.pageState.currentPage.NationalOrganWaitlistReportState) editState!: NationalOrganWaitlistReportState;

  @State(state => state.lookups.organ) organLookup!: Organ[];
  @State(state => state.lookups.blood_type) bloodTypeLookup!: BloodType[];
  @State(state => state.lookups.ctr_now_facility_filter) ctrNowFacilityFilter!: GenericCodeValue[];
  @State(state => state.users.user) user!: User;

  @Getter('hospitalOptionsOntarioTransplant', { namespace: 'hospitals' }) hospitalOptionsOntarioTransplant!: HospitalOption[];

  @Getter('allOptionsPlaceholderValue', { namespace: 'reports' }) allOptionsPlaceholderValue!: (state: any, disabled?: boolean) => string;

  public mounted(): void {
    this.$store.dispatch('hospitals/load', ACTIVE_REGION_TRANSPLANT_PROGRAM).then(() => {
      this.$store.commit('pageState/set', {
        pageKey: 'NationalOrganWaitlistReportState',
        value: this.buildNationalOrganWaitlistPageState(),
      });
    });
  }

  public buildNationalOrganWaitlistPageState(): NationalOrganWaitlistReportState {
    return {
      ageFrom: 1,
      ageTo: 100,
      highStatus: false,
      iposOnly: false,
    };
  }

  get sortedFacilityFilter() {
    return this.ctrNowFacilityFilter.sort((a: GenericCodeValue, b: GenericCodeValue) => {
      return a.value.localeCompare(b.value);
    });
  }

  get filteredOrganLookup() {
    // remove vessels for banking and VCA
    // And remove Pancreas Islets as per ATU-10037
    return this.organLookup.filter((organ: Organ) => {
      return ![OrganCodeValue.VesselsForBanking, OrganCodeValue.VCA, OrganCodeValue.PancreasIslets].includes(organ.code);
    });
  }

  get bloodTypeOptions(): BloodType[]|undefined {
    return this.bloodTypeLookup.filter((blood: BloodType) => {
      return ![BloodTypeValue.Unknown.toString(), BloodTypeValue.Fetus.toString()].includes(blood.code);
    });
  }

  private nowListLoaded() {
    const saveToolbar = this.$refs.generateReport as SaveToolbar;
    saveToolbar.stopSaving( { success: true } );
  }

  private nowListError() {
    const saveToolbar = this.$refs.generateReport as SaveToolbar;
    saveToolbar.stopSaving( { success: false } );
  }

  public generateReport(): void {
    this.$emit('clear');
    const saveToolbar = this.$refs.generateReport as SaveToolbar;
    const nationalOrganWaitlistTable = this.$refs.nationalOrganWaitlistTable as NationalOrganWaitlistTable;
    saveToolbar.startSaving();

    // extract payload from editState
    const organObjects = this.editState.organTypes?.map((organCode: number): any => {
      return { code: organCode };
    });
    const isDwlEligible = this.editState.iposOnly || this.editState.highStatus ? true : undefined;

    const payload = {
      age_from: this.editState.ageFrom,
      age_to: this.editState.ageTo,
      blood_groups: this.editState.bloodTypes || [],
      dwl_eligible: isDwlEligible,
      organs: organObjects || [],
      opos: this.editState.odoCodes || [],
    };

    nationalOrganWaitlistTable.loadData(payload);
  }

  // API response keys on the left, id for our UI on the right
  public idLookup: IdLookup = {
    'age_from'      : 'national_organ_waitlist-age_from',
    'age_to'        : 'national_organ_waitlist-age_to',
    'high_status'   : 'national_organ_waitlist-high_status',
    'ipos_only'     : 'national_organ_waitlist-ipos_only',
    'blood_type'    : 'national_organ_waitlist-blood_type',
    'organ_types'   : 'national_organ_waitlist-organ_types',
    'odo_codes'     : 'national_organ_waitlist-odo_codes',
  }
}
